import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import AgeRestrictionBadge from '@/components/shared/AgeRestrictionBadge';
import createModalWrapper from '@/modals/components/createModalWrapper';
import Modal from '@/modals/components/Modal';
import { modalMotionPresets } from '@/modals/components/Modal/modalMotion';
import { cn } from '@/utils/cn';
import { useCloseAuthModal } from '../hooks/useCloseAuthModal';
import CompleteRegistration from './components/CompleteRegistration';
import { RegisterPopupForm } from './components/RegisterPopupForm';
import { RegisterStep } from './type';

interface RegisterModalProps {
  onCloseAction: () => void;
  initialStep?: RegisterStep;
  initialEmail?: string;
}

const RegisterModal = createModalWrapper<RegisterModalProps>(({
  onCloseAction,
  initialStep = RegisterStep.REGISTER,
  initialEmail = '',
}) => {
  const t = useTranslations('register');
  const { handleCloseModal } = useCloseAuthModal({ page: '/game/', onCloseAction });
  const [stepOfRegistration, setStepOfRegistration] = useState<RegisterStep>(initialStep);
  const [isCompleteRegistrationMounted, setIsCompleteRegistrationMounted] = useState(initialStep === RegisterStep.COMPLETE_REGISTRATION);
  const [email, setEmail] = useState<string>(initialEmail);

  const isCompleteRegistration = stepOfRegistration === RegisterStep.COMPLETE_REGISTRATION;

  const headerTitle = useMemo(() => {
    if (isCompleteRegistration) {
      return '';
    }
    return t('title');
  }, [isCompleteRegistration, t]);
  const accessibleTitle = isCompleteRegistration ? t('request-sent') : undefined;

  const isGradientVariant = stepOfRegistration === RegisterStep.REGISTER || stepOfRegistration === RegisterStep.COMPLETE_REGISTRATION;

  return (
    <Modal
      headerTitle={headerTitle}
      accessibleTitle={accessibleTitle}
      onCloseAction={handleCloseModal}
      contentClassName={cn('lg:w-[23.75rem] lg:h-[44.6875rem] lg:rounded-3xl hide-scrollbar overflow-visible', isCompleteRegistration ? 'overflow-hidden' : '')}
      variant={isGradientVariant ? 'gradient' : 'default'}
      motionPreset={modalMotionPresets.none}
      headerChild={stepOfRegistration === RegisterStep.REGISTER ? <AgeRestrictionBadge /> : undefined}
    >
      {() => (
        <>
          {isCompleteRegistrationMounted && (
            <CompleteRegistration email={email} isActive={isCompleteRegistration} onCloseAction={onCloseAction} />
          )}

          {!isCompleteRegistration && (
            <RegisterPopupForm
              onRegistrationStartAction={() => setIsCompleteRegistrationMounted(true)}
              onRegistrationFailureAction={() => setIsCompleteRegistrationMounted(false)}
              setStepOfRegistration={setStepOfRegistration}
              setEmail={setEmail}
            />
          )}
        </>
      )}
    </Modal>
  );
});

export default RegisterModal;
